/**
 * Faturalama — Aylık Mecra Harcaması (son 6 ay ızgarası)
 *
 * Ajansın aylık fatura kontrolü için: seçili markada her mecranın harcaması,
 * satırlar fatura kalemi / sütunlar son 6 ay. Tek metrik (harcama) olduğu için
 * tablo Excel gibi dolduruluyor; aylar arası kıyas doğrudan görünüyor.
 *
 * Rakamlar DB'den DEĞİL, `/api/billing/live-spend` üzerinden platform
 * API'lerinden ANLIK çekilir (markanın tüm aktif reklam hesapları toplanır —
 * Yataş Bedding'de 2 Google + 2 Meta hesabı var, tek hesaba bakmak faturayı
 * eksik gösterir).
 *
 * Kırılım MECRA ÖNCELİKLİ: her platform kendi bloğunda (logosuyla), altında
 * Branding ve Performance satırları. Bir hücreye tıklayınca O AYIN o kovadaki
 * kampanyaları açılır.
 *
 * Google altında YouTube/Demand Gen/Search ayrımı YAPILMAZ — talep gereği tüm
 * Google harcaması tek satırda toplanır.
 *
 * Branding/Performance ayrımı `utils/brandingClassifier` ile yapılır; kural
 * orada tek yerde durur (Aylık Branding/Performans raporları da oradan besleniyor).
 *
 * Faturaya özgü iki kural — rapor sayfalarında YOK, sadece burada:
 *  1. Lokal (mağaza ziyareti) kampanyaları Meta VE Google hesaplarının içinden
 *     çıkarılıp mecranın kendi bloğunda ayrı satırda gösterilir; müşteri faturada
 *     Lokal'i ayrı kalem olarak görüyor (Google tarafı: Yataş Grubu, 19 Ağu 2026).
 *     Ayrı kalem olsa da MECRA BAŞLIĞINDAKİ toplama dahildir (Yataş Grubu,
 *     4 Eyl 2026) — başlık hesabın gerçek harcamasıdır, alttaki Branding /
 *     Performance satırları Lokal düşülmüş halidir.
 *  2. %4,5 vergi YALNIZCA Google Ads'e uygulanır (Meta/Pinterest/TikTok ve manuel
 *     kalemler hariç) — Yataş Grubu geri bildirimi, 17 Ağu 2026.
 *  3. Tek aya özel YÜK İSTİSNALARI (`YUK_ISTISNALARI`): fatura bir ay farklı
 *     kesildiyse ızgara faturaya uyar, hesabın "doğrusuna" değil. Şu an tek kayıt
 *     var: Puffy · Ağu 2026 · Meta Lokal → %5 konum ücreti eklenmez.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  ChevronLeft, ChevronRight, RefreshCw, Loader2,
  AlertTriangle, Download, Save, Plug, PencilLine, X, Plus, Trash2, Info,
} from 'lucide-react';
import { classifyCampaign } from '../../utils/brandingClassifier';
import { haricKurallari, eslesenHaricKurali } from '../../utils/haricKampanyalar';

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const hdr = (brandId) => ({
  'X-Brand-ID': brandId,
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const AY_ADLARI = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];
const AY_KISA = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

const AY_SAYISI = 6;

/* Sınıflandırıcıya verilecek platform anahtarı */
const CLASSIFIER_PLATFORM = {
  GOOGLE_ADS: 'google',
  META_ADS: 'meta',
  TIKTOK_ADS: 'tiktok',
  PINTEREST_ADS: 'pinterest',
  CRITEO_ADS: 'criteo',
  RTB_HOUSE: 'rtbhouse',
};

/* Sidebar'ın kullandığı logo dosyaları — public/ altında duruyorlar */
const PLATFORM_LOGO = {
  GOOGLE_ADS: '/google_ads-512.webp',
  META_ADS: '/6033716.png',
  PINTEREST_ADS: '/pinterestlogopng.png',
  TIKTOK_ADS: '/tik-tokiconss.png',
  CRITEO_ADS: '/CRcriteopng.png',
  RTB_HOUSE: '/RTBhouseiconPNG.png',
};

const PLATFORM_BASLIK = {
  GOOGLE_ADS: 'Google Ads',
  META_ADS: 'Meta Ads',
  PINTEREST_ADS: 'Pinterest',
  TIKTOK_ADS: 'TikTok Ads',
  CRITEO_ADS: 'Criteo',
  RTB_HOUSE: 'RTB House',
};

/**
 * API karşılığı olmayan SABİT kalemler — elle girilir, marka + ay bazında saklanır.
 * Yandex burada: sistemde YANDEX_ADS diye bir entegrasyon yok, bağlanınca
 * otomatik satıra dönüştürülebilir.
 *
 * Lokal artık burada DEĞİL: harcama Meta ve Google hesaplarının içinden geliyor,
 * aşağıda otomatik ayrıştırılıyor. Elle de girilseydi aynı para iki kez sayılırdı.
 * Markaya özel kalemler (Criteo, RTB…) `/api/billing/manual-item-defs` ucundan
 * gelir ve bu listenin altına eklenir.
 */
const MANUEL_KALEMLER = [
  { key: 'branding_yandex', bucket: 'branding', label: 'Branding Yandex Yayın Bedeli' },
  { key: 'performance_yandex', bucket: 'performance', label: 'Performance Yandex Yayın Bedeli' },
  { key: 'branding_medya_satin_alma', bucket: 'branding', label: 'Medya Satın Alma Yayın Bedeli' },
];

/**
 * Mecra başına vergi / stopaj / muafiyet / ücret yükü. Genel toplam üzerinden tek bir
 * vergi satırı gösterme biçimi bilerek TERK EDİLDİ (Yataş, 19 Ağu 2026), geri koyma.
 *
 * Her mecra bir LİSTE tutar — aynı mecrada birden fazla yük üst üste binebilir
 * (Meta: %2,65 muafiyet + Temmuz 2026'dan itibaren %5 konum ücreti).
 *
 * İki farklı gösterim var, ikisi de müşterinin faturasını taklit ediyor:
 *  · `tur: 'dahil'` → yük doğrudan harcamanın içine yazılır, ayrı satır yok.
 *    GOOGLE: %4,5 vergi ("faturada da biz %4,5 eklenmiş halini görüyoruz").
 *    META %5 konum ücreti (Yataş, 27 Ağu 2026: "ayrı kalemde göstermeyelim,
 *    direkt harcamanın üzerine %5 ekleyelim").
 *    CRITEO %5 ücreti (Efe, 2 Eyl 2026) — Criteo'nun her ay faturaya eklediği ücret.
 *  · `tur: 'satir'` → harcama ham kalır, yük HER KALEMİN ALTINDA kendi satırında
 *    görünür (Yataş, 19 Ağu 2026: "her bir kalemin stopajını ayrı satırda görelim").
 *    META: %2,65 muafiyet · PINTEREST + TIKTOK: %15 stopaj.
 *
 * 🔴 Stopaj DÜZ ÇARPIM DEĞİL, BRÜTLEŞTİRİLEREK hesaplanır: tutar / 0,85 − tutar
 * (yani net tutarın %17,65'i). Meta'nın %2,65 muafiyeti ve %5 konum ücreti düz çarpımdır.
 *
 * 🔴🔴 SIRA: önce `dahil` yükler tutara biner, `satir` yükleri O TUTAR üzerinden
 * hesaplanır (bkz. `satirYuku`). Meta'da %2,65 muafiyetin matrahı harcama + %5
 * konum ücretidir — ham harcamaya uygularsan kalem başına %5 eksik çıkar
 * (Sıla, 7 Eyl 2026). Efektif oran ham harcamanın %2,7825'i.
 *
 * 🔴 `baslangic: 'YYYY-MM'` verilen yük SADECE o aydan itibaren uygulanır — geçmiş
 * faturalar geriye dönük bozulmaz. `satir` türünde satır çizilir ama hücre "—" kalır;
 * `dahil` türünde (Meta %5 konum ücreti) eski ayların tutarına hiç binmez.
 * META %5 KONUM ÜCRETİ: Meta 1 Temmuz 2026'dan itibaren Türkiye teslimatında konum
 * ücreti alıyor (oran, Türkiye DHV oranıyla birebir aynı: %5). Faturada subtotal'in
 * ÜSTÜNE ayrı kalem olarak ekleniyor; Reklam Yöneticisi'nde ve API'nin `spend`
 * alanında GÖRÜNMÜYOR, o yüzden buraya elle bindiriliyor. Ekranda ve CSV'de ayrı
 * satır DEĞİL, Meta kalemlerinin tutarının İÇİNDE gösterilir. Gerçek faturayla
 * doğrulandı (19 Ağu 2026): subtotal 355,96 → konum ücreti 17,77 (= %4,99).
 */
const MECRA_YUKU = {
  GOOGLE_ADS: [
    { id: 'vergi', tur: 'dahil', oran: 0.045, brut: false, etiket: '%4,5 vergi' },
  ],
  META_ADS: [
    { id: 'muafiyet', tur: 'satir', oran: 0.0265, brut: false, etiket: '%2,65 muafiyet', kalem: '%2,65 muafiyet maliyeti' },
    {
      // Yataş, 27 Ağu 2026: "ayrı kalemde göstermeyelim, direkt harcamanın üzerine
      // %5 ekleyelim" → `dahil`. Toplamlar DEĞİŞMEDİ, yalnızca gösterim değişti.
      id: 'konum', tur: 'dahil', oran: 0.05, brut: false, baslangic: '2026-07',
      etiket: "%5 konum ücreti (Tem 2026'dan)", kalem: '%5 konum ücreti',
    },
  ],
  CRITEO_ADS: [
    {
      // Criteo her ay harcamanın üzerine %5 ücret ekliyor; Meta konum ücretinde
      // olduğu gibi ayrı kalem DEĞİL, Criteo tutarının içinde gösterilir
      // (Efe, 2 Eyl 2026: "totalin üzerine ekleyelim, criteoda da bunu yapalım").
      // 🔴 `baslangic` YOK — ücret her ay alındığı için ızgaradaki 6 ayın hepsine
      // biner. Belirli bir aydan başlatılacaksa buraya `baslangic: 'YYYY-MM'` yaz.
      id: 'ucret', tur: 'dahil', oran: 0.05, brut: false, etiket: '%5 ücret', kalem: '%5 ücret',
    },
  ],
  PINTEREST_ADS: [
    { id: 'stopaj', tur: 'satir', oran: 0.15, brut: true, etiket: '%15 stopaj', kalem: '%15 stopaj (brütleştirilmiş)' },
  ],
  TIKTOK_ADS: [
    { id: 'stopaj', tur: 'satir', oran: 0.15, brut: true, etiket: '%15 stopaj', kalem: '%15 stopaj (brütleştirilmiş)' },
  ],
};
/**
 * YÜK İSTİSNALARI — tek bir marka + mecra + kalem + ay birleşiminde bir yükü
 * KALDIRIR. Sayfanın işi hesabın "doğrusunu" göstermek değil, müşterinin eline
 * geçen faturayı birebir tutturmak; bir ay farklı kesildiyse ızgara ona uyar.
 *
 * Sıla / Yataş Grubu, 7 Eyl 2026: "Puffy tarafında ağustos ayı için yalnızca
 * lokal meta tarafı düzenleyebilir misin? Fatura kesilirken %5 ekleme
 * yapılmamış. Doğrudan harcanan tutar alınabilir. Bu yalnızca Ağustos lokal
 * Meta için geçerli."
 *
 * 🔴 İstisna DÖRT anahtarın hepsiyle birden eşleşir (marka + mecra + kova + ay);
 * biri tutmazsa yük normal uygulanır. Yani Puffy'nin Eylül'ü, Puffy'nin Meta
 * Branding'i, Yataş Bedding'in Ağustos'u — hiçbiri etkilenmez.
 * 🔴🔴 Kaldırılan yük `dahil` türündeyse `satir` yüklerinin MATRAHI da düşer:
 * %2,65 muafiyet artık ham harcamadan hesaplanır (bkz. `satirYuku`). Ölçüldü
 * (puffy, Ağu 2026, canlı API — tek kampanya `Branding-LocalStoreVisit-YBB`):
 * ham 98.320,62 → eski satır 103.236,65 + muafiyet 2.735,77 = 105.972,42;
 * yeni satır 98.320,62 + muafiyet 2.605,50 = 100.926,12 (fark 5.046,31).
 */
const YUK_ISTISNALARI = {
  // puffy
  '7fc8e68d-f2ba-4101-95f7-acece386ab8f': [
    {
      id: 'puffy-2026-08-lokal-konum',
      platform: 'META_ADS', kova: 'lokal', ay: '2026-08', yukler: ['konum'],
      etiket: '%5 konum ücreti yok',
      not: 'Puffy · Ağustos 2026 · Meta Lokal: fatura %5 konum ücreti EKLENMEDEN kesildi '
        + '(Sıla / Yataş Grubu, 7 Eyl 2026), bu yüzden kalem doğrudan harcanan tutardır; '
        + '%2,65 muafiyet de o ham tutardan hesaplanır. Yalnızca bu ay ve bu kalem için '
        + 'geçerli — Puffy\'nin diğer ayları, diğer kalemleri ve diğer markalar değişmedi.',
    },
  ],
};
/** Hücreye uyan istisnalar. `baglam` = { brandId, kova }; verilmezse istisna yok. */
const istisnalar = (platformKey, ayKey, baglam) =>
  (YUK_ISTISNALARI[baglam?.brandId] || []).filter(
    (k) => k.platform === platformKey && k.ay === ayKey && k.kova === baglam?.kova
  );
/** Yük o ayda yürürlükte mi? (ay anahtarı `YYYY-MM`, düz metin karşılaştırması yeterli) */
const yukGecerli = (y, ayKey) => !y.baslangic || String(ayKey || '') >= y.baslangic;
/** Yük o hücrede gerçekten uygulanıyor mu? (yürürlükte + istisnayla kaldırılmamış) */
const yukUygulanir = (y, platformKey, ayKey, baglam) =>
  yukGecerli(y, ayKey) && !istisnalar(platformKey, ayKey, baglam).some((k) => k.yukler.includes(y.id));
/** O ayda mecraya binen yüklerin listesi — istisnayla kaldırılanlar çıkarılmış. */
const yukleri = (platformKey, ayKey, baglam) =>
  (MECRA_YUKU[platformKey] || []).filter((y) => yukUygulanir(y, platformKey, ayKey, baglam));
/** Tek bir yükün tutarı — stopajda brütleştirme, diğerlerinde düz çarpım. */
const kalemYuku = (tutar, y) => {
  const t = Number(tutar || 0);
  return y.brut ? t / (1 - y.oran) - t : t * y.oran;
};
/** `dahil` yüklerin toplamı — matrahın kendisine giren kısım. */
const dahilYukTutari = (tutar, platformKey, ayKey, baglam) =>
  yukleri(platformKey, ayKey, baglam).filter((y) => y.tur === 'dahil').reduce((s, y) => s + kalemYuku(tutar, y), 0);
/** Kalem satırında gösterilen tutar: yalnızca `dahil` yükler içeride, `satir` olanlar altta. */
const dahilTutar = (tutar, platformKey, ayKey, baglam) =>
  Number(tutar || 0) + dahilYukTutari(tutar, platformKey, ayKey, baglam);
/**
 * 🔴🔴 `satir` yüklerinin MATRAHI ham harcama DEĞİL, `dahil` yükler bindirilmiş
 * tutardır (Sıla / Yataş Grubu, 7 Eyl 2026: "muafiyet maliyetlerimiz yanlış
 * hesaplanıyor, Puffy Meta Branding 46K olması gerekirken 44K görünüyor").
 * Gerçek Meta faturasında %2,65 muafiyet, %5 konum ücreti EKLENDİKTEN SONRAKİ
 * tutardan alınıyor → efektif oran ham harcamanın %2,7825'i (1,05 × %2,65).
 * Ölçüldü (puffy, Ağu 2026, canlı API): Branding ham 1.679.189,88 →
 * eski hesap 44.498,53 · doğrusu **46.723,46** (Sıla'nın beklediği rakam).
 * Sıra ÖNEMLİ: önce `dahil` (Meta %5 konum, Google %4,5 vergi, Criteo %5 ücret),
 * sonra `satir` (Meta %2,65 muafiyet, Pinterest/TikTok %15 stopaj). Aynı kural
 * grup faturalandırma sayfasında da var ("mecra ücreti stopaj matrahının İÇİNDE").
 * `dahil` yükü olmayan mecralarda (Pinterest, TikTok) matrah ham harcamadır,
 * yani o satırlar DEĞİŞMEZ; Temmuz 2026 öncesi Meta'da da konum ücreti
 * yürürlükte olmadığı için eski aylar aynı kalır.
 */
const satirYuku = (tutar, y, platformKey, ayKey, baglam) =>
  kalemYuku(dahilTutar(tutar, platformKey, ayKey, baglam), y);
/** Mecrada tutarın İÇİNE giren bir yük var mı? (ipucu metni için — ay filtresi yok) */
const dahilYukleriVar = (platformKey) => (MECRA_YUKU[platformKey] || []).some((y) => y.tur === 'dahil');
/** Kalemin üstüne binen TÜM yüklerin toplamı (o ay için). */
const yukTutari = (tutar, platformKey, ayKey, baglam) =>
  dahilYukTutari(tutar, platformKey, ayKey, baglam)
  + yukleri(platformKey, ayKey, baglam)
    .filter((y) => y.tur === 'satir')
    .reduce((s, y) => s + satirYuku(tutar, y, platformKey, ayKey, baglam), 0);
const yukluTutar = (tutar, platformKey, ayKey, baglam) =>
  Number(tutar || 0) + yukTutari(tutar, platformKey, ayKey, baglam);
/**
 * Birden çok kalemden oluşan tutar (mecra başlığı, ay toplamı) — HER PARÇA kendi
 * kovasıyla yüklenir, sonra toplanır. Yükler tutara göre DOĞRUSAL olduğu için
 * istisna yokken sonuç, parçaları toplayıp tek seferde yüklemekle birebir aynı;
 * istisna varken tek seferde hesaplarsak istisnalı kova da yükü yerdi.
 * `parcalar` = [[kova, tutar], …]
 */
const parcaliYuklu = (parcalar, platformKey, ayKey, brandId) =>
  parcalar.reduce((s, [kova, tutar]) => s + yukluTutar(tutar, platformKey, ayKey, { brandId, kova }), 0);
const parcaliYuk = (parcalar, platformKey, ayKey, brandId) =>
  parcalar.reduce((s, [kova, tutar]) => s + yukTutari(tutar, platformKey, ayKey, { brandId, kova }), 0);
/** Kendi satırında gösterilen yükler — ızgarada gösterildiği için ay filtresi YOK
 *  (henüz başlamamış yükün satırı çizilir, hücresi "—" olur). */
const yukSatirlari = (platformKey) => (MECRA_YUKU[platformKey] || []).filter((y) => y.tur === 'satir');
/**
 * Ekranda ve CSV'de aynı cümle görünsün diye tek yerde. Yataş'ın Temmuz 2026
 * faturasında ilk kez çıkan kalem — sayfa Haziran ve öncesini DEĞİŞTİRMEZ.
 */
const KONUM_UCRETI_NOTU =
  "Meta konum ücreti: Meta 1 Temmuz 2026'dan itibaren Türkiye'de yayınlanan reklamlarda %5 konum ücreti alıyor "
  + '(oran, Türkiye dijital hizmet vergisi oranıyla aynı). Ücret harcamanın üzerine faturada ayrı kalem olarak '
  + "ekleniyor; Reklam Yöneticisi'nde ve platform API'sinin harcama rakamında GÖRÜNMÜYOR. Bu yüzden Temmuz 2026 ve "
  + 'sonrası aylarda Meta kalemlerinin tutarına %5 konum ücreti doğrudan eklendi (ayrı satır olarak gösterilmiyor). '
  + 'Haziran 2026 ve öncesi değişmedi.';

/**
 * Criteo ücreti — Meta konum ücretiyle aynı mantık: platform API'sinin harcama
 * rakamında görünmüyor, faturaya her ay ayrıca biniyor. Ekranda ve CSV'de ayrı
 * satır DEĞİL, Criteo kalemlerinin tutarının içinde.
 */
const CRITEO_UCRETI_NOTU =
  'Criteo ücreti: Criteo harcamanın üzerine her ay %5 ücret ekliyor. Bu ücret platform API\'sinin '
  + 'harcama rakamında GÖRÜNMEDİĞİ için Criteo kalemlerinin tutarına doğrudan eklendi; ayrı satır '
  + 'olarak gösterilmiyor. Izgaradaki tüm aylara uygulanır.';

/** Hariç tutulan kampanyalar için ekranda ve CSV'de aynı cümle (kural LİSTESİ alır).
 *  `dipToplamaDahil` kurallar (Dormy Home, Urban Mood Sleep) ayrı faturalanır ama
 *  Genel Toplam'a girer; diğerleri hiçbir toplama girmez. */
const haricNotu = (kurallar) => {
  const adlar = (ks) => ks.map((k) => k.etiket).join(' ve ');
  const ayri = kurallar.filter((k) => k.dipToplamaDahil);
  const disari = kurallar.filter((k) => !k.dipToplamaDahil);
  const parcalar = [];
  if (ayri.length) {
    parcalar.push(`${adlar(ayri)}: Bu kampanyalar AYRI faturalanır. Branding / Performance / Lokal `
      + 'kalemlerinden çıkarıldı, mecranın altında kendi satırında gösteriliyor; tutarları (vergi + '
      + 'muafiyet + ücret dahil) MECRA TOPLAMINA ve dipte ayrı satırda toplanıp GENEL TOPLAM\'a eklenir.');
  }
  if (disari.length) {
    parcalar.push(`${adlar(disari)}: Bu kampanyalar faturaya DAHİL DEĞİLDİR. Branding / Performance / `
      + 'Lokal kalemlerinden ve TÜM toplamlardan çıkarıldı; mecranın altında yalnızca bilgi olarak '
      + 'gösteriliyor (o satırdaki tutar vergi + muafiyet + ücret dahildir).');
  }
  return parcalar.join(' ');
};

/** Mecra başlığındaki rozet metni — birden fazla yük varsa hepsi yazılır. */
const yukEtiketi = (platformKey) => (MECRA_YUKU[platformKey] || []).map((y) => y.etiket).join(' + ');

/** Hariç kalemleri hücrede `haric_<id>` anahtarıyla durur; kova adı da aynı. */
const haricKova = (kural) => `haric_${kural.id}`;
const isHaricKova = (bucket) => String(bucket || '').startsWith('haric_');

/**
 * Lokal (mağaza ziyareti) kampanyaları. Faturada ayrı kalem oldukları için
 * Branding / Performance satırlarından düşülüp KENDİ satırlarında gösterilirler.
 *
 * 🔴 Ama mecra BAŞLIĞINDAKİ toplama DAHİLDİR (Sıla / Yataş Grubu, 4 Eyl 2026:
 * "Meta toplamı kısmında lokali de dahil edebilir miyiz? Sadece branding meta
 * kısmını topluyoruz"). Yani mecra başlığı = Branding + Performance + Lokal =
 * hesabın gerçek harcaması. Eskiden başlık Lokal'i DIŞARIDA bırakıyordu; alttaki
 * "Branding - Pazarlama TOPLAM" ise Lokal'i İÇERİ alıyordu — iki toplam
 * birbirini tutmuyor, müşteri mecra başlığını fatura tutarı sanıp Lokal'i
 * atlıyordu. Ölçüldü (Divanev, Ağu 2026): başlık 832.326,58 ₺ yazıyordu,
 * Lokal + muafiyeti olan 83.229,82 ₺ dışarıda kalıyordu → doğrusu 915.556,40 ₺.
 *
 * Meta ad kalıpları ölçüldü (Ağu 2026): Bedding `DH - [Store Visit] - Branding -
 * LocalStoreAwareness`, Puffy `Branding-LocalStoreVisit-YBB`, Divanev
 * `DH-[Store Visit]-Maltepe-Bayi`, `Branding-Reach-Local-YBB`.
 * Objective'e bakılmıyor: Meta objective'i bu kampanyalarda OUTCOME_AWARENESS
 * dönüyor, mağaza ziyaretini normal erişimden ayırmıyor.
 *
 * Google ad kalıpları ölçüldü (19 Ağu 2026, 175 marka şeması tarandı — aynı ad
 * kuralı Google'da da tutuyor, YANLIŞ eşleşme yok): Bedding `Branding - PMax -
 * Local Store Visit - Lokal - Ankara`, `Branding - Demand Gen - Maps - Local
 * Store Visit`, Puffy `Branding - Pmax - Local Store Visit - New`, Divanev
 * `DH-[Pmax]-Local Visit`, EnzaHome `DH - [Pmax] - Store Visit - Branding`,
 * Chakra `Store Visit - İstanbul Akasya Avm`. Kampanya TİPİNE bakılmıyor: bunlar
 * ayrı bir "Local" kanal tipi değil, hedefi mağaza ziyareti olan PMax/Demand Gen.
 * Google lokali de vergiye tabidir — %4,5 matrahı Google satırı + Lokal satırıdır.
 */
const LOKAL_AYRISAN_PLATFORMLAR = new Set(['META_ADS', 'GOOGLE_ADS']);
const LOKAL_ROZET = { META_ADS: "Meta'dan ayrıldı", GOOGLE_ADS: "Google'dan ayrıldı" };
const KOVA_NOKTA = { branding: 'bg-violet-500', performance: 'bg-emerald-500', lokal: 'bg-sky-500' };
const kovaNoktasi = (bucket) => KOVA_NOKTA[bucket] || 'bg-slate-300';
const kovaEtiketi = (bucket, platformLabel, haricKurallar = []) => {
  if (bucket === 'lokal') return 'Lokal Yayın Bedeli';
  if (isHaricKova(bucket)) {
    const k = haricKurallar.find((x) => haricKova(x) === bucket);
    return `${k?.etiket || 'Hariç'} Yayın Bedeli`;
  }
  return `${bucket === 'branding' ? 'Branding' : 'Performance'} ${platformLabel} Yayın Bedeli`;
};

const isLokalKampanya = (name) => {
  const s = String(name || '').toLowerCase();
  const sikistirilmis = s.replace(/[^a-z0-9]+/g, ''); // LocalStoreVisit → localstorevisit
  if (/storevisit|localstore/.test(sikistirilmis)) return true;
  return /(^|[^a-z])(local|lokal)([^a-z]|$)/.test(s); // "…-Local-YBB", "…-Lokal"
};

/* Izgarada para simgesi tekrarlanmıyor — sadece sayı, sağa dayalı */
const num = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n, currency = 'TRY') => {
  const v = Number(n || 0);
  try {
    return v.toLocaleString('tr-TR', { style: 'currency', currency, maximumFractionDigits: 2 });
  } catch {
    return `${num(v)} ${currency}`;
  }
};
const saatFmt = (iso) =>
  iso ? new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const ayAnahtar = (y, m) => `${y}-${String(m).padStart(2, '0')}`;

/* ── Ana sayfa ──────────────────────────────────────────────────────── */

export default function BillingSpend() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || localStorage.getItem('currentBrandId') || '';
  // Bu markada faturadan hariç tutulan kampanya kuralları (yoksa boş → hiçbir şey elenmez)
  const haricKurallar = useMemo(() => haricKurallari(brandId), [brandId]);
  /** Genel Toplam'a kendi satırıyla eklenen hariç kurallar (Dormy Home, Urban Mood Sleep). */
  const dipToplamKurallari = useMemo(() => haricKurallar.filter((k) => k.dipToplamaDahil), [haricKurallar]);

  const now = new Date();
  // Fatura kontrolü kapanmış ay için yapılır → ızgaranın SON sütunu bir önceki ay.
  const gecenAy = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [endYear, setEndYear] = useState(gecenAy.getFullYear());
  const [endMonth, setEndMonth] = useState(gecenAy.getMonth() + 1);

  const [platforms, setPlatforms] = useState([]);
  const [platformsLoading, setPlatformsLoading] = useState(false);
  const [cells, setCells] = useState({});               // `${ayAnahtar}|${platform}` -> {loading,error,data}
  const [manualByMonth, setManualByMonth] = useState({}); // ayAnahtar -> {itemKey: amount|null}
  const [dirtyMonths, setDirtyMonths] = useState([]);    // kaydedilecek aylar
  const [manualSaving, setManualSaving] = useState(false);
  const [manualSavedAt, setManualSavedAt] = useState(null);
  const [ozelKalemler, setOzelKalemler] = useState([]);  // markaya eklenmiş mecralar (Criteo, RTB…)
  const [kalemFormu, setKalemFormu] = useState(null);    // {label, bucket} | null
  const [kalemKaydediliyor, setKalemKaydediliyor] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [detay, setDetay] = useState(null); // {platformKey, bucket, ay}

  const reqIdRef = useRef(0); // marka/ay değişince eski yanıtların state'i ezmesini engeller

  /* Izgara sütunları — eskiden yeniye 6 ay */
  const aylar = useMemo(() => {
    const out = [];
    for (let i = AY_SAYISI - 1; i >= 0; i--) {
      const d = new Date(endYear, endMonth - 1 - i, 1);
      out.push({
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        key: ayAnahtar(d.getFullYear(), d.getMonth() + 1),
        kisa: AY_KISA[d.getMonth()],
        uzun: `${AY_ADLARI[d.getMonth()]} ${d.getFullYear()}`,
      });
    }
    return out;
  }, [endYear, endMonth]);

  /* ── Veri çekimi ─────────────────────────────────────────────── */

  const fetchAll = useCallback(async () => {
    if (!brandId) return;
    const reqId = ++reqIdRef.current;
    setPageError(null);
    setPlatformsLoading(true);
    setCells({});
    setManualByMonth({});
    setDirtyMonths([]);
    setDetay(null);
    setPlatforms([]);
    setKalemFormu(null);
    setOzelKalemler([]);

    // Markaya özel manuel kalemler ay bazlı değil — bir kez okunur.
    try {
      const res = await fetch(`${API_BASE}/api/billing/manual-item-defs`, {
        credentials: 'include',
        headers: hdr(brandId),
      });
      if (res.ok) {
        const json = await res.json();
        if (reqId !== reqIdRef.current) return;
        setOzelKalemler(json.items || []);
      }
    } catch (e) {
      console.error('[billing] özel kalemler okunamadı', e);
    }

    let connected = [];
    try {
      const res = await fetch(`${API_BASE}/api/billing/platforms`, {
        credentials: 'include',
        headers: hdr(brandId),
      });
      if (!res.ok) throw new Error(`Mecra listesi alınamadı (${res.status})`);
      const json = await res.json();
      if (reqId !== reqIdRef.current) return;
      setPlatforms(json.platforms || []);
      connected = (json.platforms || []).filter((p) => p.connected);
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      setPageError(e.message);
      setPlatformsLoading(false);
      return;
    }
    setPlatformsLoading(false);

    const bekleyen = {};
    for (const ay of aylar) for (const p of connected) bekleyen[`${ay.key}|${p.key}`] = { loading: true };
    setCells(bekleyen);

    // AY AY, en yeniden eskiye. Ay içinde mecralar paralel; aylar sıralı, çünkü
    // 6 ay × tüm mecralar aynı anda gidince Meta/TikTok hız sınırına takılıyor.
    const sirali = [...aylar].reverse();
    for (const ay of sirali) {
      if (reqId !== reqIdRef.current) return;
      await Promise.all(
        connected.map(async (p) => {
          const hucre = `${ay.key}|${p.key}`;
          try {
            const params = new URLSearchParams({ platform: p.key, year: String(ay.year), month: String(ay.month) });
            const res = await fetch(`${API_BASE}/api/billing/live-spend?${params}`, {
              credentials: 'include',
              headers: hdr(brandId),
            });
            const json = await res.json().catch(() => ({}));
            if (reqId !== reqIdRef.current) return;
            if (!res.ok) throw new Error(json.error || `Hata (${res.status})`);
            setCells((prev) => ({ ...prev, [hucre]: { loading: false, data: json } }));
          } catch (e) {
            if (reqId !== reqIdRef.current) return;
            setCells((prev) => ({ ...prev, [hucre]: { loading: false, error: e.message } }));
          }
        })
      );

      // Aynı ayın manuel kalemleri
      try {
        const params = new URLSearchParams({ year: String(ay.year), month: String(ay.month) });
        const res = await fetch(`${API_BASE}/api/billing/manual-items?${params}`, {
          credentials: 'include',
          headers: hdr(brandId),
        });
        if (res.ok) {
          const json = await res.json();
          if (reqId !== reqIdRef.current) return;
          const map = {};
          for (const it of json.items || []) map[it.key] = it.amount;
          setManualByMonth((prev) => ({ ...prev, [ay.key]: map }));
        }
      } catch (e) {
        console.error('[billing] manuel kalem okunamadı', ay.key, e);
      }
    }
  }, [brandId, aylar]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setManualCell = (ayKey, itemKey, value) => {
    setManualByMonth((prev) => ({
      ...prev,
      [ayKey]: { ...(prev[ayKey] || {}), [itemKey]: value === '' ? null : Number(value) },
    }));
    setDirtyMonths((prev) => (prev.includes(ayKey) ? prev : [...prev, ayKey]));
  };

  /** Sabit satırlar + markaya eklenmiş özel mecralar, tabloda göründükleri sırayla */
  const manuelKalemler = useMemo(
    () => [...MANUEL_KALEMLER, ...ozelKalemler.map((o) => ({ ...o, ozel: true }))],
    [ozelKalemler]
  );

  const saveManual = useCallback(async () => {
    if (!brandId || !dirtyMonths.length) return;
    setManualSaving(true);
    try {
      for (const ayKey of dirtyMonths) {
        const ay = aylar.find((a) => a.key === ayKey);
        if (!ay) continue;
        const vals = manualByMonth[ayKey] || {};
        const items = manuelKalemler.map((m) => ({
          key: m.key,
          bucket: m.bucket,
          amount: vals[m.key] ?? null,
          currency: 'TRY',
        }));
        const res = await fetch(`${API_BASE}/api/billing/manual-items`, {
          method: 'PUT',
          credentials: 'include',
          headers: hdr(brandId),
          body: JSON.stringify({ year: ay.year, month: ay.month, items }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || `Kaydedilemedi (${res.status})`);
        }
      }
      setDirtyMonths([]);
      setManualSavedAt(new Date().toISOString());
    } catch (e) {
      setPageError(e.message);
    } finally {
      setManualSaving(false);
    }
  }, [brandId, dirtyMonths, manualByMonth, aylar, manuelKalemler]);

  /* ── Markaya özel manuel mecralar (Criteo, RTB…) ─────────────── */

  const kalemEkle = useCallback(async () => {
    const label = (kalemFormu?.label || '').trim();
    if (!brandId || !label) return;
    setKalemKaydediliyor(true);
    try {
      const res = await fetch(`${API_BASE}/api/billing/manual-item-defs`, {
        method: 'POST',
        credentials: 'include',
        headers: hdr(brandId),
        body: JSON.stringify({ label, bucket: kalemFormu.bucket }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `Kalem eklenemedi (${res.status})`);
      setOzelKalemler((prev) => [...prev, json.item]);
      setKalemFormu(null);
    } catch (e) {
      setPageError(e.message);
    } finally {
      setKalemKaydediliyor(false);
    }
  }, [brandId, kalemFormu]);

  const kalemSil = useCallback(async (kalem) => {
    if (!brandId) return;
    // Tanımla birlikte tüm ayların tutarları da silinir — geri alınamaz.
    if (!window.confirm(`"${kalem.label}" kalemi ve girilmiş tüm aylık tutarları silinsin mi?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/billing/manual-item-defs/${encodeURIComponent(kalem.key)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: hdr(brandId),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `Kalem silinemedi (${res.status})`);
      setOzelKalemler((prev) => prev.filter((o) => o.key !== kalem.key));
      setManualByMonth((prev) => {
        const out = {};
        for (const [ayKey, vals] of Object.entries(prev)) {
          const { [kalem.key]: _silinen, ...kalan } = vals;
          out[ayKey] = kalan;
        }
        return out;
      });
    } catch (e) {
      setPageError(e.message);
    }
  }, [brandId]);

  /* ── Izgara verisinin türetilmesi ────────────────────────────── */

  /** `${ay}|${platform}` → { branding:{amount,campaigns}, performance:{...}, total, currency, state } */
  const hucreler = useMemo(() => {
    const out = {};
    for (const [anahtar, st] of Object.entries(cells)) {
      const platformKey = anahtar.split('|')[1];
      const gelenKampanyalar = st.data?.campaigns || [];
      // 🔴 ÖNCE hariç tutulanlar ayrılır (Yataş Bedding'de Dormy Home + Kazakistan).
      // Bunlar hiçbir kovaya, hiçbir toplama girmez — her kural kendi satırında.
      const haricGruplar = {};
      const tumKampanyalar = [];
      for (const c of gelenKampanyalar) {
        const k = haricKurallar.length ? eslesenHaricKurali(c.name, haricKurallar) : null;
        if (k) (haricGruplar[k.id] = haricGruplar[k.id] || []).push(c);
        else tumKampanyalar.push(c);
      }
      // Lokal, Meta ve Google'da ayrıştırılır (TikTok/Pinterest'te mağaza ziyareti
      // kampanyası yok). Ayrıştırma ADA bakar; kampanya tipi/objective'i değil.
      const lokalKampanyalar = LOKAL_AYRISAN_PLATFORMLAR.has(platformKey)
        ? tumKampanyalar.filter((c) => isLokalKampanya(c.name))
        : [];
      const campaigns = lokalKampanyalar.length
        ? tumKampanyalar.filter((c) => !isLokalKampanya(c.name))
        : tumKampanyalar;
      const split = { branding: [], performance: [] };
      for (const c of campaigns) {
        const bucket = classifyCampaign({
          name: c.name,
          platform: CLASSIFIER_PLATFORM[platformKey],
          campaignType: c.campaignType,
          objective: c.objective,
          brandId,
        });
        split[bucket].push(c);
      }
      out[anahtar] = {
        state: st,
        yukVar: !!MECRA_YUKU[platformKey],
        currency: st.data?.currencies?.[0] || 'TRY',
        accounts: st.data?.accounts || [],
        // 'db' = 90 günden eski Pinterest ayı gibi, kayıtlı veriden okundu
        source: st.data?.source || 'live',
        partial: !!st.data?.partial,   // hesapların bir kısmı hata verdi → tutar EKSİK
        failed: !!st.data?.failed,     // hiçbir hesap gelmedi → rakam yok
        accountErrors: st.data?.accountErrors || [],
        // Mecra başlığındaki toplam. 🔴 Lokal DAHİL: aşağıdaki Branding /
        // Performance satırlarından düşülmüş olsa da fatura tutarı hesabın
        // TAMAMI (Yataş Grubu, 4 Eyl 2026). Hariç tutulanlar (Dormy, Kazakistan)
        // yine dışarıda — onlar faturaya hiç girmiyor.
        total: [...campaigns, ...lokalKampanyalar].reduce((s, c) => s + Number(c.spend || 0), 0),
        branding: {
          amount: split.branding.reduce((s, c) => s + Number(c.spend || 0), 0),
          campaigns: split.branding.sort((a, b) => b.spend - a.spend),
        },
        performance: {
          amount: split.performance.reduce((s, c) => s + Number(c.spend || 0), 0),
          campaigns: split.performance.sort((a, b) => b.spend - a.spend),
        },
        // Faturada ayrı kalem; toplamlarda Branding'e yazılır (kampanyalar
        // brandingClassifier'da da branding'e düşüyordu — genel toplam değişmez).
        lokal: {
          amount: lokalKampanyalar.reduce((s, c) => s + Number(c.spend || 0), 0),
          campaigns: lokalKampanyalar.sort((a, b) => b.spend - a.spend),
        },
        // Faturaya GİRMEZ — yalnızca bilgi satırı (bkz. HARIC_KAMPANYALAR).
        ...Object.fromEntries(haricKurallar.map((k) => {
          const liste = haricGruplar[k.id] || [];
          return [haricKova(k), {
            amount: liste.reduce((s, c) => s + Number(c.spend || 0), 0),
            campaigns: liste.sort((a, b) => b.spend - a.spend),
          }];
        })),
      };
    }
    return out;
  }, [cells, brandId, haricKurallar]);

  const bagliMecralar = useMemo(() => platforms.filter((p) => p.connected), [platforms]);

  /** Mecra bazında: o mecrada 6 ayın hiçbirinde lokal harcaması yoksa satır çizilmez */
  const lokalVar = useMemo(() => {
    const out = {};
    for (const platformKey of LOKAL_AYRISAN_PLATFORMLAR) {
      out[platformKey] = aylar.some((a) => (hucreler[`${a.key}|${platformKey}`]?.lokal?.amount || 0) > 0);
    }
    return out;
  }, [aylar, hucreler]);

  /** Aynı mantık hariç kalemleri için — mecrada 6 ayın hiçbirinde harcaması olmayan
   *  kuralın satırı çizilmez. Değer, o mecrada satırı olacak KURALLARIN listesi. */
  const haricVar = useMemo(() => {
    const out = {};
    for (const p of platforms) {
      // `gizli` kurallar satır ÜRETMEZ (harcamaları yine de her toplamdan düşülür).
      out[p.key] = haricKurallar.filter((k) => !k.gizli
        && aylar.some((a) => (hucreler[`${a.key}|${p.key}`]?.[haricKova(k)]?.amount || 0) > 0));
    }
    return out;
  }, [aylar, hucreler, platforms, haricKurallar]);

  /** Ekranda gerçekten satırı olan hariç kuralları — dip nottaki cümle bunlara göre. */
  const gorunenHaricKurallar = useMemo(
    () => haricKurallar.filter((k) => bagliMecralar.some((p) => (haricVar[p.key] || []).some((x) => x.id === k.id))),
    [haricKurallar, haricVar, bagliMecralar]
  );

  /** Izgarada gerçekten görünen yük istisnaları — dipteki uyarı ve CSV notu bunlara göre.
   *  (Ay penceresi kaydırılıp Ağustos ekrandan çıkarsa uyarı da kaybolur.) */
  const gorunenIstisnalar = useMemo(
    () => (YUK_ISTISNALARI[brandId] || []).filter(
      (k) => aylar.some((a) => a.key === k.ay) && bagliMecralar.some((p) => p.key === k.platform)
    ),
    [brandId, aylar, bagliMecralar]
  );

  /**
   * Ay bazında faturaya giden toplamlar. Vergi/muafiyet mecra satırlarının İÇİNDE
   * olduğu için toplamlar da yüklü gelir; `vergiBranding` / `vergiPerformance`
   * yalnızca "bu toplamın ne kadarı vergi" bilgi satırı içindir, TEKRAR EKLENMEZ.
   */
  const toplamlar = useMemo(() => {
    const out = {};
    for (const ay of aylar) {
      let branding = 0;
      let performance = 0;
      let vergiBranding = 0;
      let vergiPerformance = 0;
      for (const p of bagliMecralar) {
        const h = hucreler[`${ay.key}|${p.key}`];
        if (!h) continue;
        // 🔴 Branding toplamı Lokal'i İÇERİR ama tek tutar olarak yüklenemez:
        // yük istisnası kova bazında (Puffy Ağu 2026 Meta Lokal) — her parça
        // kendi kovasıyla yüklenir. İstisna yokken sonuç birebir aynı.
        const brandingParcalari = [['branding', h.branding.amount], ['lokal', h.lokal.amount]];
        const performansParcalari = [['performance', h.performance.amount]];
        branding += parcaliYuklu(brandingParcalari, p.key, ay.key, brandId);
        performance += parcaliYuklu(performansParcalari, p.key, ay.key, brandId);
        vergiBranding += parcaliYuk(brandingParcalari, p.key, ay.key, brandId);
        vergiPerformance += parcaliYuk(performansParcalari, p.key, ay.key, brandId);
      }
      for (const mk of manuelKalemler) {
        const v = Number(manualByMonth[ay.key]?.[mk.key] || 0);
        if (mk.bucket === 'branding') branding += v;
        else performance += v;
      }
      // Alt markalar (Dormy Home, Urban Mood Sleep): Branding / Performance
      // faturalarına girmez ama Genel Toplam'a eklenir (Sıla, 7 Eki 2026).
      // Tutar tüm yükler dahil — mecranın altındaki gri satırla birebir aynı.
      const altMarkalar = {};
      for (const k of dipToplamKurallari) {
        const kova = haricKova(k);
        altMarkalar[k.id] = bagliMecralar.reduce((s, p) => s + yukluTutar(
          hucreler[`${ay.key}|${p.key}`]?.[kova]?.amount || 0, p.key, ay.key, { brandId, kova }
        ), 0);
      }
      const genel = branding + performance + Object.values(altMarkalar).reduce((s, v) => s + v, 0);
      out[ay.key] = {
        branding, performance, genel, altMarkalar,
        vergiBranding, vergiPerformance,
        vergi: vergiBranding + vergiPerformance,
      };
    }
    return out;
  }, [aylar, bagliMecralar, hucreler, manualByMonth, manuelKalemler, brandId, dipToplamKurallari]);

  const anyLoading = platformsLoading || Object.values(cells).some((s) => s.loading);

  const yabanciParalar = useMemo(() => {
    const set = new Set();
    for (const st of Object.values(cells)) {
      for (const cur of st.data?.currencies || []) if (cur && cur !== 'TRY') set.add(cur);
    }
    return [...set];
  }, [cells]);

  /* CSV — ızgaranın birebir kopyası (Excel'e yapıştırmak için) */
  const downloadCsv = useCallback(() => {
    const satir = (hucreListesi) => hucreListesi.join(';');
    const lines = [satir(['Fatura Kalemi', ...aylar.map((a) => a.uzun)])];
    for (const p of bagliMecralar) {
      const yukSatirlariListesi = yukSatirlari(p.key);
      const ham = (a, alan) =>
        alan === 'total' ? hucreler[`${a.key}|${p.key}`]?.total : hucreler[`${a.key}|${p.key}`]?.[alan]?.amount;
      const bicim = (n) => Number(n || 0).toFixed(2).replace('.', ',');
      // Izgaradaki gösterimin birebir aynısı: mecra başlığı yüklü, kalem satırı
      // yükü ayrıysa ham, Google'da yüklü; yük satırları kalemin altında.
      const csvKalem = (a, alan) => bicim(dahilTutar(ham(a, alan), p.key, a.key, { brandId, kova: alan }));
      // Mecra başlığı: Branding + Performance + Lokal, HER PARÇA kendi kovasıyla
      // yüklenir (yük istisnaları kova bazında — bkz. YUK_ISTISNALARI).
      const basligiParcala = (a) => {
        const hu = hucreler[`${a.key}|${p.key}`];
        return [
          ['branding', hu?.branding?.amount || 0],
          ['performance', hu?.performance?.amount || 0],
          ['lokal', hu?.lokal?.amount || 0],
          // Dormy / Urban Mood: başlık, altındaki satırların toplamı olsun (Sıla, 7 Eki 2026)
          ...dipToplamKurallari.map((k) => [haricKova(k), hu?.[haricKova(k)]?.amount || 0]),
        ];
      };
      const kalemler = [
        ['branding', `Branding ${p.label} Yayın Bedeli`],
        ['performance', `Performance ${p.label} Yayın Bedeli`],
        ...(lokalVar[p.key] ? [['lokal', `Lokal Yayın Bedeli (${PLATFORM_BASLIK[p.key] || p.label} içinden)`]] : []),
      ];
      // Mecra başlığı — Lokal DAHİL toplam (bkz. LOKAL_AYRISAN_PLATFORMLAR notu).
      // Etikete açıkça yazılıyor, yoksa Excel'de "Branding + Performance" sanılıp
      // Lokal ikinci kez toplanıyor.
      lines.push(satir([
        (PLATFORM_BASLIK[p.key] || p.label)
          + (yukEtiketi(p.key) ? ` (${yukEtiketi(p.key)})` : '')
          + (lokalVar[p.key] ? ' — TOPLAM, Lokal dahil' : '')
          + (dipToplamKurallari.some((k) => (haricVar[p.key] || []).some((x) => x.id === k.id))
            ? ` + ${dipToplamKurallari.filter((k) => (haricVar[p.key] || []).some((x) => x.id === k.id)).map((k) => k.etiket).join(' + ')} dahil`
            : ''),
        ...aylar.map((a) => bicim(parcaliYuklu(basligiParcala(a), p.key, a.key, brandId))),
      ]));
      for (const [alan, etiket] of kalemler) {
        lines.push(satir([etiket, ...aylar.map((a) => csvKalem(a, alan))]));
        // Yükü ayrı satırdaki mecralarda her yük kendi satırında (Meta'da %2,65
        // muafiyet; %5 konum ücreti kalemin tutarının içinde, ayrı satırı yok).
        for (const y of yukSatirlariListesi) {
          lines.push(satir([
            `  ${etiket.replace(' Yayın Bedeli', '')} — ${y.kalem}`,
            ...aylar.map((a) => (yukUygulanir(y, p.key, a.key, { brandId, kova: alan })
              ? bicim(satirYuku(ham(a, alan), y, p.key, a.key, { brandId, kova: alan }))
              : '')),
          ]));
        }
      }
      // Hariç tutulan kampanyalar (Dormy Home, Kazakistan) — mecranın ALTINDA, her
      // kural kendi satırında; toplama girmez. Tutar TÜM yükler dahil yazılır ki
      // ayrıca faturalanacaksa doğrudan kullanılsın.
      for (const k of haricVar[p.key] || []) {
        lines.push(satir([
          `${k.etiket} — FATURAYA DAHİL DEĞİL (${PLATFORM_BASLIK[p.key] || p.label} içinden)`,
          ...aylar.map((a) => bicim(yukluTutar(ham(a, haricKova(k)), p.key, a.key, { brandId, kova: haricKova(k) }))),
        ]));
      }
    }
    for (const mk of manuelKalemler) {
      lines.push(satir([mk.label, ...aylar.map((a) => {
        const v = manualByMonth[a.key]?.[mk.key];
        return v == null ? '' : Number(v).toFixed(2).replace('.', ',');
      })]));
    }
    lines.push('');
    lines.push(satir(['Branding - Pazarlama TOPLAM', ...aylar.map((a) => (toplamlar[a.key]?.branding ?? 0).toFixed(2).replace('.', ','))]));
    lines.push(satir(['Performance - E-Ticaret TOPLAM', ...aylar.map((a) => (toplamlar[a.key]?.performance ?? 0).toFixed(2).replace('.', ','))]));
    for (const k of dipToplamKurallari) {
      lines.push(satir([`${k.etiket} TOPLAM (ayrı fatura, Genel Toplam'a dahil)`, ...aylar.map((a) => (toplamlar[a.key]?.altMarkalar?.[k.id] ?? 0).toFixed(2).replace('.', ','))]));
    }
    lines.push(satir(['GENEL TOPLAM (VERGİ + MUAFİYET + ÜCRET DAHİL)', ...aylar.map((a) => (toplamlar[a.key]?.genel ?? 0).toFixed(2).replace('.', ','))]));
    lines.push(satir(['- içindeki vergi/muafiyet/ücret (Branding)', ...aylar.map((a) => (toplamlar[a.key]?.vergiBranding ?? 0).toFixed(2).replace('.', ','))]));
    lines.push(satir(['- içindeki vergi/muafiyet/ücret (Performance)', ...aylar.map((a) => (toplamlar[a.key]?.vergiPerformance ?? 0).toFixed(2).replace('.', ','))]));

    lines.push('');
    lines.push(satir([KONUM_UCRETI_NOTU]));
    if (bagliMecralar.some((p) => p.key === 'CRITEO_ADS')) lines.push(satir([CRITEO_UCRETI_NOTU]));
    if (gorunenHaricKurallar.length) lines.push(satir([haricNotu(gorunenHaricKurallar)]));
    // Tek aya özel yük istisnaları — Excel'e yapıştıran kişi rakamın neden
    // "eksik" göründüğünü CSV'nin içinde görsün.
    for (const k of gorunenIstisnalar) lines.push(satir([k.not]));

    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `fatura-${(selectedBrand?.name || 'marka').replace(/\s+/g, '-')}-son${AY_SAYISI}ay-${ayAnahtar(endYear, endMonth)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [aylar, bagliMecralar, hucreler, manualByMonth, manuelKalemler, lokalVar, haricVar,
      gorunenHaricKurallar, gorunenIstisnalar, toplamlar, selectedBrand, endYear, endMonth, brandId, dipToplamKurallari]);

  const shiftMonth = (delta) => {
    const d = new Date(endYear, endMonth - 1 + delta, 1);
    setEndYear(d.getFullYear());
    setEndMonth(d.getMonth() + 1);
  };

  /* ── Hücre ───────────────────────────────────────────────────── */

  const Hucre = ({ ay, platformKey, bucket, vurgu, yuk }) => {
    const h = hucreler[`${ay.key}|${platformKey}`];
    const st = h?.state || {};
    const kova = bucket ? h?.[bucket] : null;
    const hamDeger = bucket ? kova?.amount : h?.total;
    /* Dört durum:
       · yuk verildi (yük satırı) → SADECE o yükün tutarı; yük o ayda henüz
         yürürlükte değilse (Meta konum ücreti, Tem 2026 öncesi) hücre "—" kalır
       · mecra başlığı            → harcama + o ayın TÜM yükleri (fatura tutarı)
       · kalem satırı             → harcama + yalnızca `dahil` yükler (Google %4,5,
                                    Meta %5 konum ücreti); ayrı satırdaki yükler
                                    (%2,65 muafiyet, %15 stopaj) hariç
       · hariç satırı (`haric_*`) → faturaya girmez; altında yük satırı çizilmediği
                                    için TÜM yükler dahil tek rakam yazılır */
    // Mecra başlığında kova yok; tutar üç kalemin toplamı olduğu için yük
    // istisnası uygulanabilsin diye parça parça yüklenir (bkz. `parcaliYuklu`).
    const baslikParcalari = [
      ['branding', h?.branding?.amount || 0],
      ['performance', h?.performance?.amount || 0],
      ['lokal', h?.lokal?.amount || 0],
      // Ayrı faturalanan alt markalar (Dormy Home, Urban Mood Sleep) da başlığa
      // girer — başlık altındaki satırların toplamıyla tutsun (Sıla, 7 Eki 2026).
      ...dipToplamKurallari.map((k) => [haricKova(k), h?.[haricKova(k)]?.amount || 0]),
    ];
    const baglam = { brandId, kova: bucket };
    // Mecra başlığında kova yok ama tutarı istisna DEĞİŞTİRİR — o yüzden başlıkta
    // o aya ait tüm istisnalar gösterilir, kalem satırında yalnızca kendi kovasınınki.
    const hucreIstisnalari = bucket
      ? istisnalar(platformKey, ay.key, baglam)
      : (YUK_ISTISNALARI[brandId] || []).filter((k) => k.platform === platformKey && k.ay === ay.key);
    const yukUygulanmaz = !!yuk && !yukUygulanir(yuk, platformKey, ay.key, baglam);
    const deger = hamDeger == null || yukUygulanmaz
      ? null
      : yuk
        ? satirYuku(hamDeger, yuk, platformKey, ay.key, baglam)
        : bucket && !isHaricKova(bucket)
          ? dahilTutar(hamDeger, platformKey, ay.key, baglam)
          : bucket
            ? yukluTutar(hamDeger, platformKey, ay.key, baglam)
            : parcaliYuklu(baslikParcalari, platformKey, ay.key, brandId);
    const acilabilir = !yuk && bucket && (kova?.campaigns?.length || 0) > 0;
    const secili = detay && detay.platformKey === platformKey && detay.bucket === bucket && detay.ay === ay.key;

    return (
      <td
        onClick={acilabilir ? () => setDetay(secili ? null : { platformKey, bucket, ay: ay.key }) : undefined}
        title={acilabilir ? `${ay.uzun} · ${kova.campaigns.length} kampanya — kırılım için tıklayın` : undefined}
        className={`border-l border-slate-100 px-3 py-1.5 text-right tabular-nums
          ${vurgu ? 'font-semibold text-slate-900' : 'text-slate-700'}
          ${acilabilir ? 'cursor-pointer hover:bg-indigo-50' : ''}
          ${secili ? 'bg-indigo-100 ring-1 ring-inset ring-indigo-300' : ''}`}
      >
        {st.loading ? (
          <span className="text-slate-300">…</span>
        ) : st.error || h?.failed ? (
          // Rakamı 0 diye göstermek yerine açıkça hata de — faturada sessiz sıfır
          // Pinterest'te aylarca 100 bin TL'yi yok saymıştı.
          <span
            className="text-rose-500"
            title={st.error || h.accountErrors.map((a) => `${a.name}: ${a.error}`).join('\n')}
          >
            hata
          </span>
        ) : yukUygulanmaz ? (
          // Yük o ayda henüz yürürlükte değil — 0,00 yazmak "hesaplandı ama sıfır"
          // izlenimi verirdi; boş kalem olduğunu açıkça göster.
          <span
            className="text-slate-300"
            title={hucreIstisnalari.length
              ? hucreIstisnalari.map((k) => k.not).join('\n')
              : `Bu yük ${yuk.baslangic.split('-')[1].replace(/^0/, '')}/${yuk.baslangic.split('-')[0]} ayından itibaren uygulanıyor — bu ayın faturasında yok.`}
          >
            —
          </span>
        ) : (
          <span
            className={h?.source === 'db' ? 'underline decoration-dotted decoration-slate-300 underline-offset-4' : undefined}
            title={h?.source === 'db' ? 'Bu ay platform API\'sinin canlı penceresinin dışında — kayıtlı günlük veriden okundu' : undefined}
          >
            {deger ? num(deger) : <span className="text-slate-300">0,00</span>}
            {/* Tek aya özel yük istisnası — rakam komşu aylardan farklı kuralla
                hesaplandığı için sessiz kalmıyor (bkz. YUK_ISTISNALARI). */}
            {hucreIstisnalari.length > 0 && (
              <span
                className="ml-1 cursor-help text-amber-600"
                title={hucreIstisnalari.map((k) => k.not).join('\n')}
              >
                *
              </span>
            )}
            {h?.partial && (
              <span
                className="ml-1 text-amber-600"
                title={`EKSİK — bazı hesaplar alınamadı:\n${h.accountErrors.map((a) => `${a.name}: ${a.error}`).join('\n')}`}
              >
                !
              </span>
            )}
          </span>
        )}
      </td>
    );
  };

  /* ── Render ──────────────────────────────────────────────────── */

  if (!brandId) {
    return <div className="p-8 text-slate-500">Devam etmek için üstteki menüden bir marka seçin.</div>;
  }

  const yillar = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) yillar.push(y);

  const detayHucre = detay ? hucreler[`${detay.ay}|${detay.platformKey}`] : null;
  const detayKova = detayHucre ? detayHucre[detay.bucket] : null;
  const detayAy = detay ? aylar.find((a) => a.key === detay.ay) : null;

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-6">
      {/* Başlık + dönem seçimi */}
      <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[12px] font-bold uppercase tracking-wider text-indigo-600">Faturalama</p>
          <h1 className="mt-1 text-[24px] font-bold tracking-tight text-slate-900">Aylık Mecra Harcaması</h1>
          <p className="mt-1 text-[13px] text-slate-500">
            {selectedBrand?.name || 'Seçili marka'} · {aylar[0]?.uzun} – {aylar[aylar.length - 1]?.uzun} ·
            rakamlar platform API'lerinden anlık çekilir, tutarlar TL
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
            <button onClick={() => shiftMonth(-1)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Bir ay geri">
              <ChevronLeft size={16} />
            </button>
            <span className="px-1 text-[11px] uppercase tracking-wide text-slate-400">son ay</span>
            <select
              value={endMonth}
              onChange={(e) => setEndMonth(Number(e.target.value))}
              className="rounded border-0 bg-transparent px-1.5 py-1 text-[13px] font-medium text-slate-800 outline-none"
            >
              {AY_ADLARI.map((a, i) => <option key={a} value={i + 1}>{a}</option>)}
            </select>
            <select
              value={endYear}
              onChange={(e) => setEndYear(Number(e.target.value))}
              className="rounded border-0 bg-transparent px-1.5 py-1 text-[13px] font-medium text-slate-800 outline-none"
            >
              {yillar.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={() => shiftMonth(1)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="Bir ay ileri">
              <ChevronRight size={16} />
            </button>
          </div>

          <button
            onClick={fetchAll}
            disabled={anyLoading}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {anyLoading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            Yenile
          </button>

          <button
            onClick={downloadCsv}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Download size={15} /> CSV
          </button>
        </div>
      </header>

      {pageError && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">{pageError}</div>
      )}

      {yabanciParalar.length > 0 && (
        <div className="mb-4 flex gap-2.5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[12.5px] text-rose-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <p>
            Bu markada TL dışı para biriminde reklam hesabı var ({yabanciParalar.join(', ')}).
            Tutarlar hesabın kendi para biriminde gösteriliyor, kur çevrimi YAPILMIYOR —
            toplamları TL fatura tutarı olarak kullanmayın.
          </p>
        </div>
      )}

      {anyLoading && (
        <div className="mb-4 flex items-center gap-2 text-[12.5px] text-slate-400">
          <Loader2 size={14} className="animate-spin" />
          {AY_SAYISI} ay canlı çekiliyor — sütunlar en yeni aydan geriye doğru doluyor.
        </div>
      )}

      {/* ── Izgara ──────────────────────────────────────────────── */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[900px] border-collapse text-[13px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="sticky left-0 z-10 bg-slate-50 px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Fatura Kalemi
              </th>
              {aylar.map((a, i) => (
                <th
                  key={a.key}
                  className={`w-[130px] border-l border-slate-200 px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide
                    ${i === aylar.length - 1 ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500'}`}
                >
                  {a.kisa} {String(a.year).slice(2)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {bagliMecralar.map((p) => {
              const paraBirimi = aylar.map((a) => hucreler[`${a.key}|${p.key}`]?.currency).find(Boolean) || 'TRY';
              return (
                <React.Fragment key={p.key}>
                  {/* Mecra başlığı = mecra toplamı */}
                  <tr className="border-t border-slate-200 bg-slate-50/80">
                    <td className="sticky left-0 z-10 bg-slate-50/95 px-4 py-2">
                      <span className="flex items-center gap-2">
                        {PLATFORM_LOGO[p.key] && (
                          <img src={PLATFORM_LOGO[p.key]} alt={p.label} className="h-[18px] w-[18px] shrink-0 rounded-sm object-contain" />
                        )}
                        <span className="text-[13.5px] font-bold text-slate-900">{PLATFORM_BASLIK[p.key] || p.label}</span>
                        <span className="rounded bg-slate-200/70 px-1.5 py-0.5 text-[10.5px] text-slate-600">
                          {p.accounts.length} hesap
                        </span>
                        {yukEtiketi(p.key) && (
                          <span
                            className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10.5px] text-emerald-700"
                            title="Bu mecranın başlık tutarı vergi / muafiyet / ücret DAHİL yazılır; fatura tutarıyla birebir eşleşir."
                          >
                            {yukEtiketi(p.key)} dahil
                          </span>
                        )}
                        {/* Lokal ayrı satırda ama bu toplamın içinde — rozet olmazsa
                            müşteri başlığı "Branding + Performance" sanıyor
                            (Yataş Grubu, 4 Eyl 2026). */}
                        {lokalVar[p.key] && (
                          <span
                            className="rounded bg-sky-100 px-1.5 py-0.5 text-[10.5px] text-sky-700"
                            title="Bu toplam Branding + Performance + Lokal'in tamamıdır; aşağıdaki Lokal satırı ayrı kalem olarak gösteriliyor ama bu tutarın İÇİNDE. Ayrıca eklemeyin."
                          >
                            Lokal dahil
                          </span>
                        )}
                        {/* Ayrı faturalanan alt markalar da başlığın içinde (Sıla, 7 Eki 2026) */}
                        {(haricVar[p.key] || []).filter((k) => k.dipToplamaDahil).length > 0 && (
                          <span
                            className="rounded bg-slate-200 px-1.5 py-0.5 text-[10.5px] text-slate-600"
                            title="Aşağıdaki ayrı fatura satırları (alt markalar) bu toplamın İÇİNDE. Ayrıca eklemeyin."
                          >
                            {(haricVar[p.key] || []).filter((k) => k.dipToplamaDahil).map((k) => k.etiket).join(' + ')} dahil
                          </span>
                        )}
                        {paraBirimi !== 'TRY' && (
                          <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10.5px] text-rose-700">{paraBirimi}</span>
                        )}
                      </span>
                    </td>
                    {aylar.map((a) => <Hucre key={a.key} ay={a} platformKey={p.key} bucket={null} vurgu />)}
                  </tr>

                  {[
                    'branding',
                    'performance',
                    ...(lokalVar[p.key] ? ['lokal'] : []),
                    // Hariç tutulanlar EN ALTTA, her kural kendi satırında —
                    // kovaların hiçbirine ve toplamlara girmezler.
                    ...(haricVar[p.key] || []).map(haricKova),
                  ].map((bucket) => (
                    <React.Fragment key={bucket}>
                      <tr className={`border-t border-slate-100 hover:bg-slate-50/60 ${isHaricKova(bucket) ? 'bg-slate-50/60 text-slate-400' : ''}`}>
                        <td className={`sticky left-0 z-10 px-4 py-1.5 ${isHaricKova(bucket) ? 'bg-slate-50/80' : 'bg-white'}`}>
                          <span className="flex items-center gap-2 pl-5">
                            <span className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${kovaNoktasi(bucket)}`} />
                            <span className={isHaricKova(bucket) ? 'text-slate-500' : 'text-slate-700'}>
                              {kovaEtiketi(bucket, p.label, haricKurallar)}
                            </span>
                            {isHaricKova(bucket) && (
                              <span
                                className="rounded bg-slate-200 px-1.5 py-0.5 text-[10.5px] font-medium text-slate-600"
                                title={haricNotu(haricKurallar.filter((k) => haricKova(k) === bucket))}
                              >
                                {haricKurallar.find((k) => haricKova(k) === bucket)?.dipToplamaDahil
                                  ? "ayrı fatura · Genel Toplam'a dahil"
                                  : 'faturaya dahil değil'}
                              </span>
                            )}
                            {bucket === 'lokal' && (
                              <span
                                className="rounded bg-sky-100 px-1.5 py-0.5 text-[10.5px] text-sky-700"
                                title={`Mağaza ziyareti kampanyaları ${PLATFORM_BASLIK[p.key] || p.label} hesabından geliyor; yukarıdaki Branding / Performance satırlarından düşüldü, çift sayılmıyor. En üstteki ${PLATFORM_BASLIK[p.key] || p.label} toplamına ise DAHİLDİR.`}
                              >
                                {LOKAL_ROZET[p.key] || 'Mecradan ayrıldı'}
                              </span>
                            )}
                            {/* Tek aya özel yük istisnası — satırın hangi ayı farklı
                                hesaplandığı etikette yazılı (bkz. YUK_ISTISNALARI). */}
                            {gorunenIstisnalar
                              .filter((k) => k.platform === p.key && k.kova === bucket)
                              .map((k) => (
                                <span
                                  key={k.id}
                                  className="cursor-help rounded bg-amber-100 px-1.5 py-0.5 text-[10.5px] text-amber-800"
                                  title={k.not}
                                >
                                  {AY_KISA[Number(k.ay.slice(5, 7)) - 1]} {k.ay.slice(0, 4)}: {k.etiket}
                                </span>
                              ))}
                          </span>
                        </td>
                        {aylar.map((a) => <Hucre key={a.key} ay={a} platformKey={p.key} bucket={bucket} />)}
                      </tr>

                      {/* Stopaj / muafiyet — HER KALEMİN altında, HER YÜK kendi satırında
                          (Yataş, 19 Ağu 2026). Meta'da tek satır kalır: %2,65 muafiyet.
                          %5 konum ücreti kalemin tutarının içinde (Yataş, 27 Ağu 2026).
                          🔴 Hariç tutulan kalemin ALTINA yük satırı çizilmez — o kalem
                          faturaya girmiyor, tutarı zaten yükleriyle birlikte yazılıyor. */}
                      {!isHaricKova(bucket) && yukSatirlari(p.key).map((y) => (
                        <tr key={y.id} className="border-t border-slate-100 bg-slate-50/40">
                          <td
                            className="sticky left-0 z-10 bg-slate-50/70 px-4 py-1"
                            title={
                              (y.brut
                                ? `Brütleştirilerek hesaplanır: kalem tutarı ÷ ${(1 - y.oran).toLocaleString('tr-TR')} − kalem tutarı`
                                : `Kalem tutarı × ${y.oran.toLocaleString('tr-TR')}`)
                              + (dahilYukleriVar(p.key)
                                ? ' · matrah: üstteki satırın tutarı, yani ücret eklenmiş hali'
                                : '')
                              + (y.baslangic ? ` · yalnızca ${y.baslangic} ayından itibaren` : '')
                            }
                          >
                            <span className="flex items-center gap-2 pl-10 text-[12px] text-slate-500">
                              <span>└</span>
                              <span>{kovaEtiketi(bucket, p.label, haricKurallar).replace(' Yayın Bedeli', '')} — {y.kalem}</span>
                              {y.baslangic && (
                                <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] text-indigo-700">
                                  {AY_ADLARI[Number(y.baslangic.slice(5, 7)) - 1]} {y.baslangic.slice(0, 4)}'dan itibaren
                                </span>
                              )}
                            </span>
                          </td>
                          {aylar.map((a) => <Hucre key={a.key} ay={a} platformKey={p.key} bucket={bucket} yuk={y} />)}
                        </tr>
                      ))}

                      {/* Tıklanan hücrenin kampanya kırılımı — satırın hemen altında */}
                      {detay && detay.platformKey === p.key && detay.bucket === bucket && (
                        <tr className="border-t border-indigo-100 bg-indigo-50/40">
                          <td colSpan={aylar.length + 1} className="px-4 py-3">
                            <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold text-indigo-800">
                              {detayAy?.uzun} · {kovaEtiketi(bucket, p.label, haricKurallar).replace(' Yayın Bedeli', '')}
                              <span className="font-normal text-indigo-500">
                                {detayKova?.campaigns.length} kampanya · {money(detayKova?.amount, detayHucre?.currency)}
                              </span>
                              {detayHucre?.yukVar && (
                                <span
                                  className="rounded bg-white px-1.5 py-0.5 text-[10.5px] font-normal text-indigo-500"
                                  title="Kampanya kırılımı platformdan geldiği gibi, vergi/muafiyet HARİÇ gösterilir; yukarıdaki satır tutarı vergi dahildir."
                                >
                                  kampanya tutarları vergi hariç
                                </span>
                              )}
                              <button onClick={() => setDetay(null)} className="ml-auto rounded p-1 text-indigo-500 hover:bg-indigo-100" title="Kapat">
                                <X size={14} />
                              </button>
                            </div>
                            <table className="w-full text-[12px]">
                              <thead>
                                <tr className="text-left text-[11px] uppercase tracking-wide text-indigo-400">
                                  <th className="pb-1.5 font-semibold">Kampanya</th>
                                  {(detayHucre?.accounts.length || 0) > 1 && <th className="pb-1.5 font-semibold">Hesap</th>}
                                  <th className="pb-1.5 text-right font-semibold">Harcama</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detayKova?.campaigns.map((c) => (
                                  <tr key={`${c.accountId}-${c.id}`} className="border-t border-indigo-100">
                                    <td className="py-1.5 pr-4 text-slate-700">{c.name}</td>
                                    {(detayHucre?.accounts.length || 0) > 1 && (
                                      <td className="py-1.5 pr-4 text-slate-400">{c.accountName}</td>
                                    )}
                                    <td className="py-1.5 text-right tabular-nums text-slate-700">
                                      {money(c.spend, c.currency || detayHucre?.currency)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}

                </React.Fragment>
              );
            })}

            {/* Manuel kalemler */}
            <tr className="border-t border-slate-200 bg-amber-50/60">
              <td className="sticky left-0 z-10 bg-amber-50/90 px-4 py-2">
                <span className="flex items-center gap-2">
                  <PencilLine size={15} className="shrink-0 text-amber-600" />
                  <span className="text-[13.5px] font-bold text-slate-900">Manuel Kalemler</span>
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10.5px] text-amber-700">API karşılığı yok</span>
                </span>
              </td>
              <td colSpan={aylar.length} className="border-l border-slate-100 px-3 py-2 text-right">
                {dirtyMonths.length > 0 && (
                  <span className="mr-2 text-[11.5px] text-amber-700">{dirtyMonths.length} ay kaydedilmedi</span>
                )}
                {manualSavedAt && dirtyMonths.length === 0 && (
                  <span className="mr-2 text-[11.5px] text-slate-400">Son kayıt: {saatFmt(manualSavedAt)}</span>
                )}
                <button
                  onClick={() => setKalemFormu(kalemFormu ? null : { label: '', bucket: 'branding' })}
                  className="mr-2 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-[12px] font-semibold text-amber-700 hover:bg-amber-50"
                >
                  <Plus size={13} /> Mecra ekle
                </button>
                <button
                  onClick={saveManual}
                  disabled={manualSaving || !dirtyMonths.length}
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-2.5 py-1 text-[12px] font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
                >
                  {manualSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  Kaydet
                </button>
              </td>
            </tr>

            {/* Yeni mecra tanımlama — Criteo, RTB gibi API karşılığı olmayan kalemler */}
            {kalemFormu && (
              <tr className="border-t border-amber-200 bg-amber-50/40">
                <td colSpan={aylar.length + 1} className="px-4 py-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      autoFocus
                      value={kalemFormu.label}
                      onChange={(e) => setKalemFormu({ ...kalemFormu, label: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') kalemEkle(); if (e.key === 'Escape') setKalemFormu(null); }}
                      placeholder="Mecra adı — örn. Criteo, RTB House"
                      maxLength={60}
                      className="w-64 rounded-md border border-amber-300 px-2.5 py-1.5 text-[13px] outline-none focus:ring-1 focus:ring-amber-400"
                    />
                    <select
                      value={kalemFormu.bucket}
                      onChange={(e) => setKalemFormu({ ...kalemFormu, bucket: e.target.value })}
                      className="rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-[13px] outline-none"
                    >
                      <option value="branding">Branding – Pazarlama</option>
                      <option value="performance">Performance – E-Ticaret</option>
                    </select>
                    <button
                      onClick={kalemEkle}
                      disabled={kalemKaydediliyor || !kalemFormu.label.trim()}
                      className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-2.5 py-1.5 text-[12px] font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
                    >
                      {kalemKaydediliyor ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                      Ekle
                    </button>
                    <button onClick={() => setKalemFormu(null)} className="rounded-md px-2 py-1.5 text-[12px] text-slate-500 hover:bg-slate-100">
                      Vazgeç
                    </button>
                    <span className="text-[11.5px] text-slate-400">Kalem bu markanın tüm aylarında görünür.</span>
                  </div>
                </td>
              </tr>
            )}

            {manuelKalemler.map((mk) => (
              <tr key={mk.key} className="group border-t border-slate-100 hover:bg-slate-50/60">
                <td className="sticky left-0 z-10 bg-white px-4 py-1.5">
                  <span className="flex items-center gap-2 pl-5">
                    <span className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${KOVA_NOKTA[mk.bucket]}`} />
                    <span className="text-slate-700">{mk.label}</span>
                    {mk.ozel && (
                      <button
                        onClick={() => kalemSil(mk)}
                        title="Kalemi ve tüm aylık tutarlarını sil"
                        className="rounded p-0.5 text-slate-300 opacity-0 hover:bg-rose-50 hover:text-rose-600 focus:opacity-100 group-hover:opacity-100"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </span>
                </td>
                {aylar.map((a) => (
                  <td key={a.key} className="border-l border-slate-100 p-0">
                    <input
                      type="number"
                      step="0.01"
                      value={manualByMonth[a.key]?.[mk.key] ?? ''}
                      placeholder="—"
                      onChange={(e) => setManualCell(a.key, mk.key, e.target.value)}
                      className="w-full bg-transparent px-3 py-1.5 text-right tabular-nums outline-none focus:bg-amber-50 focus:ring-1 focus:ring-inset focus:ring-amber-300"
                    />
                  </td>
                ))}
              </tr>
            ))}

            {/* Faturaya giden toplamlar */}
            <tr className="border-t-2 border-slate-300 bg-violet-50/70">
              <td className="sticky left-0 z-10 bg-violet-50/90 px-4 py-2 font-bold text-slate-900">Branding – Pazarlama</td>
              {aylar.map((a) => (
                <td key={a.key} className="border-l border-slate-100 px-3 py-2 text-right font-bold tabular-nums text-slate-900">
                  {num(toplamlar[a.key]?.branding)}
                </td>
              ))}
            </tr>
            <tr className="border-t border-slate-200 bg-emerald-50/70">
              <td className="sticky left-0 z-10 bg-emerald-50/90 px-4 py-2 font-bold text-slate-900">Performance – E-Ticaret</td>
              {aylar.map((a) => (
                <td key={a.key} className="border-l border-slate-100 px-3 py-2 text-right font-bold tabular-nums text-slate-900">
                  {num(toplamlar[a.key]?.performance)}
                </td>
              ))}
            </tr>
            {/* Alt markalar — ayrı faturalanır, Branding / Performance'a girmez ama
                Genel Toplam'a eklenir (Sıla, 7 Eki 2026). */}
            {dipToplamKurallari.map((k) => (
              <tr key={k.id} className="border-t border-slate-200 bg-slate-50">
                <td
                  className="sticky left-0 z-10 bg-slate-50 px-4 py-2 font-semibold text-slate-700"
                  title={`${k.etiket} kampanyalarının tüm mecralardaki toplamı (vergi + muafiyet + ücret dahil). Ayrı faturalandığı için Branding / Performance'a girmez, Genel Toplam'a dahildir.`}
                >
                  {k.etiket} <span className="text-[11px] font-normal text-slate-500">(ayrı fatura)</span>
                </td>
                {aylar.map((a) => (
                  <td key={a.key} className="border-l border-slate-100 px-3 py-2 text-right font-semibold tabular-nums text-slate-700">
                    {num(toplamlar[a.key]?.altMarkalar?.[k.id])}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t border-slate-300 bg-slate-100">
              <td
                className="sticky left-0 z-10 bg-slate-100 px-4 py-2 font-bold text-slate-900"
                title="Vergi, muafiyet ve konum ücreti mecra satırlarının içinde — bu rakam fatura tutarıdır."
              >
                Genel Toplam <span className="text-[11px] font-normal text-slate-500">(vergi + muafiyet + ücret dahil)</span>
              </td>
              {aylar.map((a) => (
                <td key={a.key} className="border-l border-slate-200 px-3 py-2 text-right font-bold tabular-nums text-slate-900">
                  {num(toplamlar[a.key]?.genel)}
                </td>
              ))}
            </tr>
            {/* Vergi/muafiyet artık AYRI SATIR DEĞİL — yukarıdaki her mecra satırının
                içinde. Bu satır sadece "bu toplamın ne kadarı vergi" bilgisini verir,
                toplama TEKRAR EKLENMEZ (Yataş, 19 Ağu 2026). */}
            <tr className="border-t border-slate-200 bg-slate-100">
              <td
                className="sticky left-0 z-10 bg-slate-100 px-4 pb-2.5 pt-0 text-[11.5px] text-slate-500"
                title="Yukarıdaki Branding / Performance / Genel Toplam rakamlarının İÇİNDEKİ vergi + muafiyet + ücret payı. Ayrıca eklenmez."
              >
                └ içindeki vergi + muafiyet + ücret (Branding / Performance)
              </td>
              {aylar.map((a) => (
                <td key={a.key} className="border-l border-slate-200 px-3 pb-2.5 pt-0 text-right text-[11.5px] tabular-nums text-slate-500">
                  {num(toplamlar[a.key]?.vergiBranding)} / {num(toplamlar[a.key]?.vergiPerformance)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Temmuz 2026'da faturaya giren Meta konum ücreti — Meta kalemlerinin İÇİNDE */}
      {bagliMecralar.some((p) => p.key === 'META_ADS') && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-indigo-200 bg-indigo-50/60 px-4 py-3 text-[12px] leading-relaxed text-slate-600">
          <Info size={14} className="mt-0.5 shrink-0 text-indigo-500" />
          <span>
            <span className="font-semibold text-slate-800">Temmuz 2026'dan itibaren:</span> {KONUM_UCRETI_NOTU.replace('Meta konum ücreti: ', '')}
          </span>
        </div>
      )}

      {/* Criteo'nun her ay eklediği %5 ücret — Criteo kalemlerinin İÇİNDE */}
      {bagliMecralar.some((p) => p.key === 'CRITEO_ADS') && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-indigo-200 bg-indigo-50/60 px-4 py-3 text-[12px] leading-relaxed text-slate-600">
          <Info size={14} className="mt-0.5 shrink-0 text-indigo-500" />
          <span>
            <span className="font-semibold text-slate-800">Criteo:</span>{' '}
            {CRITEO_UCRETI_NOTU.replace('Criteo ücreti: ', '')}
          </span>
        </div>
      )}

      {/* Tek aya özel yük istisnaları (Puffy → Ağu 2026 Meta Lokal, %5 konum ücreti yok) */}
      {gorunenIstisnalar.map((k) => (
        <div
          key={k.id}
          className="mt-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/70 px-4 py-3 text-[12px] leading-relaxed text-slate-700"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
          <span>
            <span className="font-semibold text-slate-900">Tek aya özel istisna:</span> {k.not}
          </span>
        </div>
      ))}

      {/* Faturadan ayrılan kampanyalar (Yataş Bedding → Dormy Home + Urban Mood Sleep: ayrı fatura, Genel Toplam'a dahil) */}
      {gorunenHaricKurallar.length > 0 && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-[12px] leading-relaxed text-slate-600">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-slate-500" />
          <span>
            {haricNotu(gorunenHaricKurallar)}
          </span>
        </div>
      )}

      {/* Bağlı olmayan mecralar */}
      {platforms.some((p) => !p.connected) && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-[12px] text-slate-500">
          <Plug size={14} className="text-slate-400" />
          <span>Bu markada bağlı olmayan mecralar:</span>
          {platforms.filter((p) => !p.connected).map((p) => (
            <span key={p.key} className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-500">{p.label}</span>
          ))}
          <span className="text-slate-400">· Yandex için sistemde entegrasyon yok, manuel kalemlerden girilir.</span>
        </div>
      )}

    </div>
  );
}
